import { HTMLAttributes, FC, useState, useRef, useEffect } from "react";
import clsx from "classnames";
import { compact } from "lodash-es";
import { FeaturedCard, UserSubscriptionStatus } from "@onmo/graphql/graphql";
import { StaticImage } from "../../core/StaticImage/StaticImage";
import { Dialog } from "../../core/Dialog/Dialog";
import { Typography } from "../../core/Typography/Typography";
import { TypographyVariant } from "../../../constants/typography";
import { useStyles } from "./ConsoleMobileCards.style";
import { getFeaturedCardData } from "../../../models/console";
import { Icon } from "../../core/Icon/Icon";
import PairControllerPopup from "../PairControllerPopup/PairControllerPopup";
import DownloadPopup from "../DownloadPopup/DownloadPopup";
import { isIOS } from "react-device-detect";
import { pwaInstall, isPwaInstalled } from "../../../models/system";
import { SetupDevicesPopup } from "../SetupDevicesPopup";
import { SliderContainer, SliderContainerHandle } from "../SliderContainer";
import { SLIDER_SETTINGS } from "../../../constants/console";
import {
  useActiveElement,
  useGamepadContext,
} from "../../../contexts/GamepadProvider";
import { PageRoutes } from "../../../constants/routes";
import { isAndroidApp } from "../../../utils/platform";
import { useSubscriptionNavigate } from "../../../hooks/useSubscriptionNavigate";
import { useAAASubscription } from "../../../hooks/useAAASubscription";
import { useAppSelector } from "../../hooks";
import { selectIsAuthenticated } from "@onmo/auth";
import { useTranslation } from "react-i18next";
import { PwaInstallToast } from "../PwaInstallToast/PwaInstallToast";

interface IConsoleMobileCardsProps {
  featureCards: Record<string, FeaturedCard>;
}

export const ConsoleMobileCards: FC<
  HTMLAttributes<HTMLDivElement> & IConsoleMobileCardsProps
> = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { className, featureCards, ...otherProps } = props;
  const navigateSubscription = useSubscriptionNavigate();
  const [openPairController, setOpenPairController] = useState(false);
  const [openDownloadPopup, setOpenDownloadPopup] = useState(false);
  const [isInstallingPwa, setIsInstallingPwa] = useState(false);
  const [isPwaInstallToastOpen, setIsPwaInstallToastOpen] = useState(false);
  const [isHidePwa, setIsHidePwa] = useState(
    localStorage.getItem("isHidePwa") || isPwaInstalled(),
  );
  const [isOpenIosGuide, setIsOpenIosGuide] = useState(false);
  const [isOverflowCard, setOverflowCard] = useState(false);
  const [activeDotSlide, setActiveDotSlide] = useState(0);
  const slideContentRef = useRef<HTMLDivElement>(null);
  const sliderRef = useRef<SliderContainerHandle>(null);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const { data: dataSubscription } = useAAASubscription({
    skip: !isAuthenticated,
  });
  const { gamepadEmitter, setActiveElement, playSFX, gamepads } =
    useGamepadContext();

  const activeElement = useActiveElement();
  const isActiveSlider = activeElement?.id.startsWith("slide-");
  const activeSliderRef = useRef(isActiveSlider);
  activeSliderRef.current = isActiveSlider;
  const subscriptionStatus = dataSubscription?.me?.subscription?.status;
  const isRecurring = Boolean(dataSubscription?.me?.subscription?.isRecurring);

  useEffect(() => {
    const handleAction = (action: "left" | "right") => {
      if (!activeSliderRef.current) return;
      if (action === "right") sliderRef.current?.slickNext();
      if (action === "left") sliderRef.current?.slickPrev();
    };

    gamepadEmitter.on("action", handleAction);
    return () => {
      gamepadEmitter.off("action", handleAction);
    };
  }, [gamepadEmitter, playSFX]);

  const isAnyPanelOpen =
    openPairController ||
    openDownloadPopup ||
    isOpenIosGuide ||
    isInstallingPwa;

  useEffect(() => {
    if (isAnyPanelOpen || isActiveSlider) {
      sliderRef.current?.slickPause();
    } else {
      sliderRef.current?.slickPlay();
    }
  }, [isAnyPanelOpen, isActiveSlider]);

  const onChangeSlide = (_currentSlide: number, nextSlide: number) => {
    setActiveDotSlide(nextSlide);
    if (!isActiveSlider) return;
    const elements = document.querySelectorAll(`#slide-${nextSlide}`);
    const visibleElement = Array.from(elements).find((element) => {
      const elementRect = element.getBoundingClientRect();
      return elementRect.left >= 0;
    });
    setActiveElement(visibleElement as HTMLElement);
    playSFX("movement");
  };

  const onAfterChangeSlide = (nextSlide: number) => {
    if (!isActiveSlider) return;
    const elements = document.querySelectorAll(`#slide-${nextSlide}`);
    const visibleElement = Array.from(elements).find((element) => {
      const elementRect = element.getBoundingClientRect();
      return elementRect.left >= 0;
    });
    setActiveElement(visibleElement as HTMLElement);
  };

  const onGetApp = async () => {
    const setIsInstallPwa = () => {
      localStorage.setItem("isHidePwa", "true");
      setIsHidePwa("true");
    };

    if (isIOS) {
      setIsOpenIosGuide(true);
      return;
    }
    setIsInstallingPwa(true);
    try {
      await pwaInstall(setIsInstallPwa, () => setIsPwaInstallToastOpen(true));
    } finally {
      setIsInstallingPwa(false);
    }
  };

  const dataCards = compact(
    Object.values(featureCards).map((card) => {
      const { title, assets, internalPath, id } = getFeaturedCardData(card);

      const getButton = () => {
        switch (id) {
          case "pairing_card":
            return {
              text: t("Console_Pair Now"),
              icon: "icon-linear-arrow-right",
              onClick: () => setOpenPairController(true),
            };
          case "activate_console_card":
            return {
              text: t("Console_Activate Now"),
              icon: "icon-linear-arrow-right",
              onClick: () => navigateSubscription(PageRoutes.Paywall),
            };
          case "install_pwa_card":
            return {
              text: t("Console_Download"),
              icon: "icon-linear-download",
              onClick: onGetApp,
            };

          case "play_anywhere_card":
            return {
              text: t("Console_Explore Now"),
              icon: "icon-linear-arrow-right",
              onClick: () => setOpenDownloadPopup(true),
            };
          default:
            return {
              text: t("Console_Renew Now"),
              icon: "icon-linear-arrow-right",
              onClick: () => navigateSubscription(PageRoutes.Paywall),
            };
        }
      };
      if ((isAndroidApp || isHidePwa) && id === "install_pwa_card") return null;
      if (gamepads?.length && id === "pairing_card") return null;
      if (
        id === "subscription_expiring_card" &&
        subscriptionStatus === UserSubscriptionStatus.Subscribed &&
        isRecurring
      )
        return null;
      return {
        ...getButton(),
        images: {
          src: assets?.image1X,
          src2x: assets?.image2X,
          src3x: assets?.image3X,
        },
        title,
        id,
        internalPath,
      };
    }),
  );

  useEffect(() => {
    const handler = () => {
      setOverflowCard(window.innerWidth / 350 < dataCards.length - 0.5);
    };

    handler();
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [dataCards.length]);

  const renderSliderContent = () => {
    return dataCards.map((card, idx) => (
      <div
        key={idx}
        className={classes.item}
        onClick={card.onClick}
        data-gamepad={isOverflowCard ? "slide-slick" : `slide-static`}
        role="button"
        tabIndex={-1}
        data-selected-class={classes.selected}
        id={`slide-${idx}`}
      >
        <StaticImage
          alt=""
          src={card?.images?.src}
          src2x={card?.images?.src2x}
          src3x={card?.images?.src3x}
          className={classes.img}
        />
        <div className={classes.content}>
          <Typography variant={TypographyVariant.HeadingH3Bold}>
            {card?.title}
          </Typography>
          <div className={classes.action}>
            <Typography variant={TypographyVariant.BodyB3Bold}>
              {card?.text}
            </Typography>
            <Icon name={card?.icon} />
          </div>
        </div>
      </div>
    ));
  };

  return (
    <div
      {...otherProps}
      className={clsx(classes.root, className)}
      ref={slideContentRef}
    >
      <Dialog
        open={isOpenIosGuide}
        mainAction={{
          text: t("Console_Close"),
          onClick: (event) => {
            event.stopPropagation();
            setIsOpenIosGuide(false);
          },
          id: "close-dialog",
          size: "large",
          "data-gamepad": "default-selected",
        }}
        title={t("A11y_Install on iOS")}
        className={classes.dialog}
      >
        <SetupDevicesPopup type="ios" />
      </Dialog>
      <PairControllerPopup
        open={openPairController}
        onClose={() => setOpenPairController(false)}
      />
      <DownloadPopup
        open={openDownloadPopup}
        onClose={() => setOpenDownloadPopup(false)}
      />
      <PwaInstallToast
        open={isPwaInstallToastOpen}
        onClose={() => setIsPwaInstallToastOpen(false)}
      />
      {isOverflowCard ? (
        <SliderContainer
          ref={sliderRef}
          nbSlide={dataCards?.length || 0}
          className={classes.sliderContainer}
          settings={{
            ...SLIDER_SETTINGS,
            slidesToShow: 1,
            beforeChange: onChangeSlide,
            afterChange: onAfterChangeSlide,
            lazyLoad: undefined,
            responsive: undefined,
          }}
          slideContentRef={slideContentRef}
          dotsClass={classes.dots}
          activeDot={activeDotSlide}
        >
          {renderSliderContent()}
        </SliderContainer>
      ) : (
        <div className={classes.sliderStatic}>{renderSliderContent()}</div>
      )}
    </div>
  );
};
